import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Custom subscription selector component that updates on variant changes
 */
class SubscriptionSelectorComponent extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
  }

  /**
   * Prevents repeating default variant selection attempts.
   * @type {boolean}
   */
  #defaultVariantAttempted = false;

  /**
   * Number of retry attempts when selecting the default variant.
   * @type {number}
   */
  #selectionAttempts = 0;

  /**
   * Maximum retry attempts for selecting default variant.
   * @type {number}
   */
  #maxSelectionAttempts = 5;

  /**
   * Clears any selected selling plan from the form and URL.
   * @param {HTMLFormElement | null} form
   */
  #clearSellingPlan(form = null) {
    let targetForm = form;
    if (!targetForm) {
      const productFormComponent =
        this.closest('product-form-component') || document.querySelector('product-form-component');
      targetForm = productFormComponent?.querySelector('form') ?? null;
    }

    if (targetForm) {
      const sellingPlanInput = targetForm.querySelector('input[name="selling_plan"][ref="sellingPlan"]');
      if (sellingPlanInput instanceof HTMLInputElement) {
        sellingPlanInput.value = '';
      }
    }

    const url = new URL(window.location.href);
    if (url.searchParams.has('selling_plan')) {
      url.searchParams.delete('selling_plan');
      history.replaceState({}, '', url.toString());
    }
  }

  connectedCallback() {
    // Find the section element to listen for variant updates
    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener(ThemeEvents.variantUpdate, this.updateSubscriptionOptions);
    }
    
    // Also listen at document level as fallback
    document.addEventListener(ThemeEvents.variantUpdate, this.updateSubscriptionOptions);
    
    // Initialize on load - use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.checkVisibilityAndInitialize();
      this.selectDefaultSubscriptionVariant();
    });
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener(ThemeEvents.variantUpdate, this.updateSubscriptionOptions);
    }
    document.removeEventListener(ThemeEvents.variantUpdate, this.updateSubscriptionOptions);
    
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Check visibility and initialize subscription script
   */
  checkVisibilityAndInitialize() {
    const wrapper = this.querySelector('.custom-subscription-wrapper');
    if (!wrapper) {
      return;
    }
    
    const subscriptionOptions = wrapper.querySelector('.subscription-options');
    const hasOptions = subscriptionOptions !== null;
    
    // Show/hide wrapper based on whether subscription options exist
    if (wrapper instanceof HTMLElement) {
      if (hasOptions) {
        wrapper.style.display = '';
      } else {
        wrapper.style.display = 'none';
      }
    }
    
    if (hasOptions) {
      this.initializeSubscriptionScript();
      // Sync radio button state on initial load
      this.syncRadioButtonState();
    }
  }

  /**
   * Attempts to auto-select the "Regular" size variant when subscriptions are unavailable.
   */
  selectDefaultSubscriptionVariant() {
    if (this.#defaultVariantAttempted) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('variant')) {
      this.#defaultVariantAttempted = true;
      return;
    }

    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!(variantPicker instanceof HTMLElement)) {
      this.#retryVariantSelection();
      return;
    }

    /**
     * @param {string | undefined} value
     */
    /**
     * @param {string | null | undefined} value
     */
    const normalize = (value) => value?.toString().trim().toLowerCase();
    const desiredKeywords = ['regular', 'medium'];
    /**
     * @param {string | null | undefined} value
     * @returns {boolean}
     */
    const matchesDesiredOption = (value) => {
      const normalized = normalize(value);
      if (!normalized) return false;
      return desiredKeywords.some((keyword) => normalized.includes(keyword));
    };
    /**
     * @param {HTMLInputElement} input
     * @returns {string | undefined}
     */
    const getCandidateText = (input) =>
      normalize(
        input.value ||
          input.getAttribute('data-value') ||
          input.getAttribute('aria-label') ||
          input.closest('label')?.textContent
      );

    const radios = Array.from(variantPicker.querySelectorAll('input[type="radio"]'));
    const regularRadio = radios.find(
      (input) => input instanceof HTMLInputElement && matchesDesiredOption(getCandidateText(input)) && input.dataset.optionAvailable !== 'false'
    );

    if (regularRadio instanceof HTMLInputElement) {
      this.#defaultVariantAttempted = true;

      if (regularRadio.checked) return;

      regularRadio.checked = true;
      regularRadio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const selects = Array.from(variantPicker.querySelectorAll('select'));
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const option = Array.from(select.options).find((opt) => matchesDesiredOption(opt.text || opt.value));
      if (!option) continue;

      this.#defaultVariantAttempted = true;

      if (select.value === option.value) return;

      option.selected = true;
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    this.#retryVariantSelection();
  }

  /**
   * Schedules another attempt to select the default subscription variant.
   */
  #retryVariantSelection() {
    if (this.#selectionAttempts >= this.#maxSelectionAttempts) {
      this.#defaultVariantAttempted = true;
      return;
    }

    this.#selectionAttempts += 1;
    setTimeout(() => this.selectDefaultSubscriptionVariant(), 100);
  }

  /**
   * Updates subscription options when variant changes
   * @param {VariantUpdateEvent} event - The variant update event
   */
  updateSubscriptionOptions = async (event) => {
    // Check if this event is for our product
    const eventProductId = event.detail?.data?.productId;
    const ourProductId = this.dataset.productId;
    
    if (eventProductId && eventProductId !== ourProductId) {
      // Event is for a different product, ignore
      return;
    }
    
    if (!event.detail?.resource?.id) {
      return;
    }

    const variantId = event.detail.resource.id;
    const productId = eventProductId || ourProductId;
    const sectionId = this.dataset.sectionId;
    
    if (!productId || !variantId || !sectionId) {
      return;
    }

    // Abort any pending request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const existingWrapper = this.querySelector('.custom-subscription-wrapper');
    if (!existingWrapper) {
      return;
    }

    // Fetch the subscription selector block for this variant
    try {
      // Get the current product URL - use the actual product URL from the page
      const productFormComponent = document.querySelector('product-form-component');
      let productUrl = window.location.pathname;
      if (productFormComponent instanceof HTMLElement && productFormComponent.dataset.productUrl) {
        productUrl = productFormComponent.dataset.productUrl;
      }
      const baseUrl = productUrl.split('?')[0] || window.location.pathname;
      
      // Build section rendering URL - section_id should match the actual section ID
      const sectionUrl = new URL(baseUrl, window.location.origin);
      sectionUrl.searchParams.set('variant', variantId);
      sectionUrl.searchParams.set('section_id', sectionId);
      
      const response = await fetch(sectionUrl.toString(), { 
        signal: this.abortController.signal 
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the updated subscription selector in the response
      // The response will contain the full section HTML
      let newSelector = null;
      
      // Try finding in the section element
      const sectionElement = doc.querySelector(`[id*="shopify-section"]`);
      if (sectionElement) {
        newSelector = sectionElement.querySelector('subscription-selector-component .custom-subscription-wrapper') ||
                      sectionElement.querySelector('.custom-subscription-wrapper');
      }
      
      // Fallback: search the entire document
      if (!newSelector) {
        newSelector = doc.querySelector('subscription-selector-component .custom-subscription-wrapper') ||
                      doc.querySelector('.custom-subscription-wrapper');
      }
      
      if (newSelector) {
        const subscriptionOptions = newSelector.querySelector('.subscription-options');

        if (subscriptionOptions instanceof HTMLElement && existingWrapper instanceof HTMLElement) {
          // Ensure wrapper is visible
          existingWrapper.style.display = '';

          // Morph the content
          morph(existingWrapper, newSelector, { childrenOnly: true });

          // Re-initialize the subscription script after morphing
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.initializeSubscriptionScript();
              this.syncRadioButtonState();

              const currentOptions = this.querySelector(`#subscription-custom-${sectionId}`);
              let hasSubscription = false;
              if (currentOptions instanceof HTMLElement) {
                hasSubscription = currentOptions.dataset.hasSubscription === 'true';
              }
              if (!hasSubscription) {
                this.#clearSellingPlan();
              }

              this.#defaultVariantAttempted = false;
              this.#selectionAttempts = 0;
              this.selectDefaultSubscriptionVariant();
            }, 100);
          });
        } else if (existingWrapper instanceof HTMLElement) {
          // Fallback: hide if we truly have no markup for subscription options
          existingWrapper.style.display = 'none';
          this.#clearSellingPlan();
          this.selectDefaultSubscriptionVariant();
        }
      } else if (existingWrapper instanceof HTMLElement) {
        existingWrapper.style.display = 'none';
        this.#clearSellingPlan();
        this.selectDefaultSubscriptionVariant();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }
      console.error('Error updating subscription options:', error);
    }
  };

  /**
   * Initialize subscription selection script
   */
  initializeSubscriptionScript() {
    const sectionId = this.dataset.sectionId;
    const subscriptionOptions = this.querySelector(`#subscription-custom-${sectionId}`);
    
    if (!subscriptionOptions) {
      console.warn('Subscription options not found for section:', sectionId);
      return;
    }
    
    // Find the product form - try multiple selectors
    const productFormComponent =
      this.closest('product-form-component') || document.querySelector('product-form-component');
    const form = productFormComponent?.querySelector('form');
    
    if (!(form instanceof HTMLFormElement)) {
      console.warn('Product form not found for subscription selector');
      return;
    }

    // Remove existing listeners by cloning the element
    const newSubscriptionOptions = /** @type {HTMLElement} */ (subscriptionOptions.cloneNode(true));
    if (subscriptionOptions.parentNode) {
      subscriptionOptions.parentNode.replaceChild(newSubscriptionOptions, subscriptionOptions);
    }

    const hasSubscription = newSubscriptionOptions.dataset.hasSubscription === 'true';

    // Handle subscription selection
    /**
     * @param {HTMLInputElement} input
     */
    const handleSubscriptionChange = (input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input.disabled) return;
      
      // Find the selling_plan input with ref attribute (as required by product-form-component)
      let sellingPlanInput = form.querySelector('input[name="selling_plan"][ref="sellingPlan"]');
      
      if (input.value === 'subscribe') {
        if (!hasSubscription) {
          this.#clearSellingPlan(form);
          return;
        }

        const label = input.closest('.subscription-option');
        const priceSpan = label?.querySelector('.subscription-price[data-planid]');
        const subscriptionPlanId = priceSpan?.getAttribute('data-planid');
        
        if (subscriptionPlanId) {
          if (!sellingPlanInput) {
            // Create input if it doesn't exist
            const newInput = document.createElement('input');
            newInput.type = 'hidden';
            newInput.name = 'selling_plan';
            newInput.setAttribute('ref', 'sellingPlan');
            form.appendChild(newInput);
            sellingPlanInput = newInput;
          }
          if (sellingPlanInput instanceof HTMLInputElement) {
            sellingPlanInput.value = subscriptionPlanId;
          }
          
          // Update URL
          const url = new URL(window.location.href);
          url.searchParams.set('selling_plan', subscriptionPlanId);
          history.replaceState({}, '', url.toString());
        } else {
          this.#clearSellingPlan(form);
        }
      } else {
        this.#clearSellingPlan(form);
      }
    };

    // Listen for radio button changes
    newSubscriptionOptions.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      
      if (target.matches('input[type="radio"][name="purchase-option"]')) {
        handleSubscriptionChange(target);
      }
    });
    
    // Intercept form submission to ensure selling_plan is correct
    const formComponent = form.closest('product-form-component');
    if (formComponent && newSubscriptionOptions instanceof HTMLElement) {
      // Listen for form submission to ensure selling_plan is set correctly
      form.addEventListener('submit', (e) => {
        const selectedRadio = newSubscriptionOptions.querySelector('input[type="radio"][name="purchase-option"]:checked');
        if (selectedRadio instanceof HTMLInputElement) {
          handleSubscriptionChange(selectedRadio);
        }
      }, { capture: true });
    }
    
    // Also handle clicks on labels (in case label is clicked instead of radio)
    if (newSubscriptionOptions instanceof HTMLElement) {
      const optionLabels = newSubscriptionOptions.querySelectorAll('.subscription-option');
      optionLabels.forEach((label) => {
        if (label instanceof HTMLElement) {
          label.addEventListener('click', (e) => {
            const target = e.target;
            // Only handle if click wasn't directly on the input
            if (target instanceof HTMLElement && target.tagName !== 'INPUT') {
              const input = label.querySelector('input[type="radio"]');
              if (input instanceof HTMLInputElement && !input.disabled) {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          });
        }
      });
    }

    if (!hasSubscription) {
      const subscribeOption = newSubscriptionOptions.querySelector('input[value="subscribe"]');
      if (subscribeOption instanceof HTMLInputElement) {
        subscribeOption.checked = false;
      }
      const oneTimeOption = newSubscriptionOptions.querySelector('input[value="one-time"]');
      if (oneTimeOption instanceof HTMLInputElement) {
        oneTimeOption.checked = true;
      }
      this.#clearSellingPlan(form);
    }
  }

  /**
   * Sync radio button state with current selling plan from URL or form
   * Ensures radio button selection matches what will actually be added to cart
   */
  syncRadioButtonState() {
    const sectionId = this.dataset.sectionId;
    const subscriptionOptions = this.querySelector(`#subscription-custom-${sectionId}`);
    if (!subscriptionOptions) return;

    const formElement = document.querySelector('product-form-component form');
    if (!(formElement instanceof HTMLFormElement)) return;

    // Check for selling plan in URL first (most authoritative)
    const urlParams = new URLSearchParams(window.location.search);
    const urlSellingPlan = urlParams.get('selling_plan');
    
    // Check for selling plan in form
    const sellingPlanInput = formElement.querySelector('input[name="selling_plan"][ref="sellingPlan"]');
    const formSellingPlan = sellingPlanInput instanceof HTMLInputElement ? sellingPlanInput.value : '';
    
    // URL takes precedence, then form value
    const currentSellingPlan = urlSellingPlan || formSellingPlan;

    // Get the available plan ID for this variant
    const subscribeOption = subscriptionOptions.querySelector('input[value="subscribe"]');
    const subscribeLabel = subscribeOption?.closest('.subscription-option');
    const priceSpan = subscribeLabel?.querySelector('.subscription-price[data-planid]');
    const availablePlanId = priceSpan?.getAttribute('data-planid');

    if (currentSellingPlan && availablePlanId && currentSellingPlan === availablePlanId) {
      // Current selling plan matches this variant's available plan - select subscribe
      if (subscribeOption instanceof HTMLInputElement) {
        subscribeOption.checked = true;
      }
      const oneTimeOption = subscriptionOptions.querySelector('input[value="one-time"]');
      if (oneTimeOption instanceof HTMLInputElement) {
        oneTimeOption.checked = false;
      }
    } else {
      // No selling plan, or plan doesn't match this variant - default to one-time
      const oneTimeOption = subscriptionOptions.querySelector('input[value="one-time"]');
      if (oneTimeOption instanceof HTMLInputElement) {
        oneTimeOption.checked = true;
      }
      if (subscribeOption instanceof HTMLInputElement) {
        subscribeOption.checked = false;
      }
      
      // If there's a selling plan that doesn't match, clear it
      if (currentSellingPlan && currentSellingPlan !== availablePlanId) {
        this.#clearSellingPlan(formElement);
      }
    }
  }
}

if (!customElements.get('subscription-selector-component')) {
  customElements.define('subscription-selector-component', SubscriptionSelectorComponent);
}